import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import { EnumOperadora, useOperadoraCombo } from '@model/Operadora';
import { IConsumoInternetFilter, IConsumoInternetRecord, useRelatorioService } from '@services/RelatorioService';
import { currentCompetence } from '@utils/Functions';
import { yup } from '@validations/Yup';
import React from 'react';
import { ConsumoInternetFilter } from './ConsumoInternetFilter';
import { ConsumoInternetDetalhesModal } from './ConsumoInternetHistoricoModal';
import { ConsumoInternetTable } from './ConsumoInternetTable';

export type RelatorioConsumoInternetProps = {
}

export function RelatorioConsumoInternet (props: RelatorioConsumoInternetProps) {
    const data = useData<IConsumoInternetRecord, IConsumoInternetFilter>({
        filter: {
            competencia: useField(currentCompetence()),
            ddd: useField(),
            numeroSemMascara: useField(),
            empresaId: useField(EnumEmpresa.None),
            operadoraId: useField(EnumOperadora.None),
            cliente: useField(),
            numeroContrato: useField()
        },

        sortField: {
            name: "numeroSemMascara",
            direction: EnumSortDirection.Asc
        }
    })

    const detalhesModal = useModalWindow();

    const loading = useLoading();
    const message = useMessage();
    const relatorioService = useRelatorioService();
    const empresaCombo = useEmpresaCombo();
    const operadoraCombo = useOperadoraCombo();

    const validation = useValidation<IConsumoInternetFilter>({
        competencia: yup.string().required()
    })

    const handleCsvButtonClick = React.useCallback(() => {
        if (!loading.isLoading) {
            validation.validate(data.requestedData.filter)
                .then(() => {
                    loading.start();
                    relatorioService.exportarConsumoInternet(data.requestedData)
                        .catch(message.showBlobException)
                        .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
            }
    }, [data.filter.setErrorsFromYup, data.requestedData, loading, message.showBlobException, relatorioService, validation]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            const values = data.requestedData.filter;

            validation.validate(values)
                .then(() => {
                    loading.start();
                    relatorioService.listarConsumoInternet(data.requestedData)
                        .then(data.handleResultResponse)
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(data.filter.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(data.filter.setErrorsFromYup);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        empresaCombo.loadOptions();
        operadoraCombo.loadOptions();
    })

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Relatório de Consumo de Internet" />
                </PageTitle>

                <PageContent>
                    <ConsumoInternetFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        empresaCombo={empresaCombo}
                        operadoraCombo={operadoraCombo}
                    />
                    
                    <ConsumoInternetTable
                        data={data}
                        isLoading={loading.isLoading}
                        detalhesModal={detalhesModal}
                        onCsvButtonClick={handleCsvButtonClick}
                    />
                </PageContent>
            </Main>

            {
                data.focusedRow !== undefined && data.filter.fields.competencia.value !== undefined && (
                    <React.Fragment>
                        <ConsumoInternetDetalhesModal 
                            linha={data.focusedRow}
                            competencia={data.filter.fields.competencia.value}
                            modal={detalhesModal}
                        />
                    </React.Fragment>
                )
            }
        </React.Fragment>
    )
}